import type { LayoutElement } from "@jod/schemas";
import { sortByPaintOrder } from "@jod/schemas";
import { Maximize, Minus, Plus } from "lucide-react";
import { useCallback, useRef } from "react";
import { cn } from "../../lib/utils.ts";
import { FloorPlanDefs } from "./defs.tsx";
import { ElementShape } from "./element-shapes.tsx";
import { angleFromCenter, type Point, resizeToPointer, snapAngle } from "./geometry.ts";
import type { ElementTone, FloorPlanProps } from "./types.ts";
import { usePanZoom } from "./use-pan-zoom.ts";

const DRAG_THRESHOLD = 4; // screen px before a press becomes a drag
const NUDGE = 6; // floor units per arrow key press

type WorldFn = (clientX: number, clientY: number) => Point;

function resolveTone(
  el: LayoutElement,
  selected: boolean,
  highlighted: boolean,
  status?: string,
): ElementTone {
  if (selected) return "selected";
  if (highlighted) return "highlight";
  if (el.kind === "table") {
    return (status ?? el.status ?? "available") as ElementTone;
  }
  return "default";
}

interface NodeProps {
  el: LayoutElement;
  mode: "view" | "edit";
  selected: boolean;
  highlighted: boolean;
  status?: string;
  screenToWorld: WorldFn;
  onSelect?: (id: string | null) => void;
  onElementClick?: (id: string, el: LayoutElement) => void;
  onElementChange?: (id: string, patch: Partial<LayoutElement>) => void;
}

function ElementNode({
  el,
  mode,
  selected,
  highlighted,
  status,
  screenToWorld,
  onSelect,
  onElementClick,
  onElementChange,
}: NodeProps) {
  const drag = useRef<{
    sx: number;
    sy: number;
    ex: number;
    ey: number;
    wx: number;
    wy: number;
    moved: boolean;
  } | null>(null);
  const tone = resolveTone(el, selected, highlighted, status);

  const onPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // Select on press so a drag also selects (handles + properties appear).
    onSelect?.(el.id);
    const w = screenToWorld(e.clientX, e.clientY);
    drag.current = {
      sx: e.clientX,
      sy: e.clientY,
      ex: el.cx,
      ey: el.cy,
      wx: w.x,
      wy: w.y,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < DRAG_THRESHOLD) return;
    d.moved = true;
    if (mode === "edit" && onElementChange) {
      const w = screenToWorld(e.clientX, e.clientY);
      onElementChange(el.id, { cx: d.ex + (w.x - d.wx), cy: d.ey + (w.y - d.wy) });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (d && !d.moved) {
      onSelect?.(el.id);
      onElementClick?.(el.id, el);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(el.id);
      onElementClick?.(el.id, el);
      return;
    }
    if (mode !== "edit" || !onElementChange) return;
    const map: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE, 0],
      ArrowRight: [NUDGE, 0],
      ArrowUp: [0, -NUDGE],
      ArrowDown: [0, NUDGE],
    };
    const delta = map[e.key];
    if (delta) {
      e.preventDefault();
      onElementChange(el.id, { cx: el.cx + delta[0], cy: el.cy + delta[1] });
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: interactive SVG group cannot be a real <button>
    <g
      transform={`translate(${el.cx} ${el.cy}) rotate(${el.rotation})`}
      role="button"
      tabIndex={0}
      aria-label={`${el.kind} ${el.label ?? ("code" in el ? el.code : "")}`.trim()}
      aria-pressed={selected}
      style={{ cursor: mode === "edit" ? "grab" : "pointer", outline: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onKeyDown={onKeyDown}
    >
      <ElementShape el={el} tone={tone} />
    </g>
  );
}

/** Resize corners + rotate handle for the selected element (edit mode only). */
function SelectionHandles({
  el,
  scale,
  screenToWorld,
  onElementChange,
}: {
  el: LayoutElement;
  scale: number;
  screenToWorld: WorldFn;
  onElementChange?: (id: string, patch: Partial<LayoutElement>) => void;
}) {
  const hx = el.w / 2;
  const hy = el.h / 2;
  const r = 9 / scale; // constant screen size
  const rotateOffset = 30 / scale;

  const startGesture = useCallback(
    (e: React.PointerEvent, kind: "resize" | "rotate") => {
      e.stopPropagation();
      const move = (ev: PointerEvent) => {
        const w = screenToWorld(ev.clientX, ev.clientY);
        if (kind === "resize") {
          onElementChange?.(el.id, resizeToPointer(el, w));
        } else {
          const deg = angleFromCenter(el, w) + 90;
          onElementChange?.(el.id, { rotation: snapAngle(deg) });
        }
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [el, screenToWorld, onElementChange],
  );

  const corners: { x: number; y: number; cursor: string }[] = [
    { x: -hx, y: -hy, cursor: "nwse-resize" },
    { x: hx, y: -hy, cursor: "nesw-resize" },
    { x: hx, y: hy, cursor: "nwse-resize" },
    { x: -hx, y: hy, cursor: "nesw-resize" },
  ];

  return (
    <g
      transform={`translate(${el.cx} ${el.cy}) rotate(${el.rotation})`}
      style={{ pointerEvents: "all" }}
    >
      <rect
        x={-hx}
        y={-hy}
        width={el.w}
        height={el.h}
        fill="none"
        stroke="var(--lime-500)"
        strokeWidth={1.5 / scale}
        strokeDasharray={`${6 / scale} ${4 / scale}`}
        rx={6}
        style={{ pointerEvents: "none" }}
      />
      <line
        x1={0}
        y1={-hy}
        x2={0}
        y2={-hy - rotateOffset}
        stroke="var(--lime-500)"
        strokeWidth={1.5 / scale}
      />
      <circle
        cx={0}
        cy={-hy - rotateOffset}
        r={r}
        fill="var(--lime-500)"
        style={{ cursor: "grab" }}
        onPointerDown={(e) => startGesture(e, "rotate")}
      />
      {corners.map((c) => (
        <rect
          key={`${c.x},${c.y}`}
          x={c.x - r}
          y={c.y - r}
          width={r * 2}
          height={r * 2}
          rx={2 / scale}
          fill="var(--cream)"
          stroke="var(--lime-500)"
          strokeWidth={1 / scale}
          style={{ cursor: c.cursor }}
          onPointerDown={(e) => startGesture(e, "resize")}
        />
      ))}
    </g>
  );
}

/**
 * Controlled SVG floor-plan renderer shared by owner / staff / customer.
 * Pan + zoom always on; drag/resize/rotate handles only in `edit` mode.
 */
export function FloorPlan({
  layout,
  mode = "view",
  selectedId = null,
  statusById,
  highlightIds,
  markerIds,
  onElementClick,
  onSelect,
  onElementChange,
  className,
}: FloorPlanProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { view, screenToWorld, onWheel, startPan, zoomBy, reset } = usePanZoom(svgRef);

  const highlightSet = new Set(highlightIds ?? []);
  const markerSet = new Set(markerIds ?? []);
  const ordered = sortByPaintOrder(layout.elements);
  const selectedEl = layout.elements.find((e) => e.id === selectedId) ?? null;

  const onBackgroundDown = (e: React.PointerEvent) => {
    onSelect?.(null);
    startPan(e);
  };

  return (
    <div
      className={cn(
        "relative h-full w-full touch-none select-none overflow-hidden rounded-[var(--radius)] border border-hairline bg-background",
        className,
      )}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${layout.floor.width} ${layout.floor.height}`}
        className="h-full w-full"
        onWheel={onWheel}
        aria-label={`Floor plan: ${layout.name}`}
      >
        <FloorPlanDefs />
        <g transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}>
          <rect
            x={0}
            y={0}
            width={layout.floor.width}
            height={layout.floor.height}
            fill="url(#fp-floor-bg)"
            onPointerDown={onBackgroundDown}
          />
          {ordered.map((el) => (
            <ElementNode
              key={el.id}
              el={el}
              mode={mode}
              selected={el.id === selectedId}
              highlighted={highlightSet.has(el.id)}
              status={statusById?.[el.id]}
              screenToWorld={screenToWorld}
              onSelect={onSelect}
              onElementClick={onElementClick}
              onElementChange={onElementChange}
            />
          ))}
          {markerSet.size > 0
            ? layout.elements
                .filter((el) => markerSet.has(el.id))
                .map((el) => (
                  <g key={`marker-${el.id}`} style={{ pointerEvents: "none" }}>
                    <circle
                      cx={el.cx}
                      cy={el.cy - el.h / 2 - 12}
                      r={9}
                      fill="var(--lime-500)"
                      opacity={0.25}
                      className="animate-ping"
                    />
                    <circle cx={el.cx} cy={el.cy - el.h / 2 - 12} r={5} fill="var(--lime-500)" />
                  </g>
                ))
            : null}
          {mode === "edit" && selectedEl ? (
            <SelectionHandles
              el={selectedEl}
              scale={view.scale}
              screenToWorld={screenToWorld}
              onElementChange={onElementChange}
            />
          ) : null}
        </g>
      </svg>

      <div className="absolute right-3 bottom-3 flex flex-col gap-1.5">
        <ZoomButton label="Zoom in" onClick={() => zoomBy(1.2)}>
          <Plus className="size-4" />
        </ZoomButton>
        <ZoomButton label="Zoom out" onClick={() => zoomBy(1 / 1.2)}>
          <Minus className="size-4" />
        </ZoomButton>
        <ZoomButton label="Reset view" onClick={reset}>
          <Maximize className="size-4" />
        </ZoomButton>
      </div>
    </div>
  );
}

function ZoomButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-9 items-center justify-center rounded-full border border-hairline bg-glass text-cream backdrop-blur transition-colors hover:bg-glass-hi"
    >
      {children}
    </button>
  );
}
